import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, StaleContentDetails } from "@/types/technicalAudit";

interface StaleContentCardProps {
	issue: PageIssue;
}

function getDaysColor(days: number): string {
	if (days >= 365) return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
	if (days >= 180) return "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
	return "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800";
}

export function StaleContentCard({ issue }: StaleContentCardProps) {
	const details = issue.issue_details as StaleContentDetails;
	const badgeClass = getDaysColor(details.days_since_update);
	const formattedDate = new Date(details.last_modified).toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Stale Content</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This page hasn't been updated in {details.days_since_update} days. Search engines prefer fresh, regularly updated content.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Last Updated
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="flex items-center justify-between">
						<span className="text-lg font-medium text-foreground">{formattedDate}</span>
						<Badge variant="outline" className={cn(badgeClass)}>
							{details.days_since_update} days ago
						</Badge>
					</div>
					<div className="p-3 bg-muted rounded-lg border border-border">
						<p className="text-sm text-muted-foreground">
							Threshold: content older than {details.threshold_days} days is flagged for review.
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
